import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useFlashMutation } from '@everlutionsk/ui-apollo';
import { Img } from '@filejet/sdk/react';
import { Box, CircularProgress, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import { Button } from '../components/coreComponents/button';

// branding.* property keys, kept in sync with the main app's branding page
// (packages/frontend/src/pages/organisationBranding.tsx). The primary logo
// is additionally stored on Organisation.logoId via accountBrandingLogoUpdate.
const faviconPropertyKey = 'branding.faviconId';
const faviconLightPropertyKey = 'branding.faviconLightId';
const faviconDarkPropertyKey = 'branding.faviconDarkId';
const logoLightPropertyKey = 'branding.logoLightId';
const logoDarkPropertyKey = 'branding.logoDarkId';

// Same six theme keys/swatches the main app exposes
// (packages/frontend/src/orgColorThemeContext.tsx).
const colorThemeOptions = [
  { key: 'default', label: 'Default', swatch: '#0C3CE4' },
  { key: 'red', label: 'Red', swatch: '#C92626' },
  { key: 'purple', label: 'Purple', swatch: '#7B1FA2' },
  { key: 'green', label: 'Green', swatch: '#1B9754' },
  { key: 'black', label: 'Black', swatch: '#111111' },
  { key: 'golden', label: 'Golden', swatch: '#B8860B' }
] as const;

const logoPreviewHeight = 80;
const logoPreviewWidth = Math.round((logoPreviewHeight * 16) / 9);
const faviconPreviewSize = 48;

interface BrandingProperty {
  readonly key: string;
  readonly value: string;
}

export function AccountBrandingTab({ accountId }: { accountId: string }) {
  const apollo = useApolloClient();
  const { data, loading, refetch } = useQuery(brandingQuery, { variables: { accountId } });
  // Tracks the property key currently uploading/removing so we can show a
  // spinner on just that variant while disabling every action meanwhile.
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const busy = busyKey != null;

  const [updateLogo] = useFlashMutation(logoUpdateMutation, { successMsg: 'Logo updated.' });
  const [updateColorTheme] = useFlashMutation(colorThemeUpdateMutation, {
    successMsg: 'Color theme updated.'
  });
  const [updateFaviconProperty] = useFlashMutation(propertiesUpdateMutation, {
    successMsg: 'Favicon updated.'
  });
  const [removeBrandingProperty] = useFlashMutation(propertiesUpdateMutation, {
    successMsg: 'Branding asset removed.'
  });
  // Silent companion to the logo flow: the logo variant property is written
  // alongside the primary logo, but only updateLogo should flash so the
  // upload surfaces a single success message.
  const [updateLogoVariantProperty] = useMutation(propertiesUpdateMutation);

  const branding = data?.accountBranding ?? null;
  const properties: readonly BrandingProperty[] = branding?.properties ?? [];
  const propertyValue = (key: string) => properties.find(p => p.key === key)?.value ?? null;

  const logo = branding?.logo ?? null;
  const logoId = logo?.id ?? null;
  const logoLightId = propertyValue(logoLightPropertyKey);
  const logoDarkId = propertyValue(logoDarkPropertyKey);
  const resolvedLightLogoId = logoLightId ?? logoDarkId ?? logoId;
  const resolvedDarkLogoId = logoDarkId ?? logoLightId ?? logoId;

  const faviconId = propertyValue(faviconPropertyKey);
  const faviconLightId = propertyValue(faviconLightPropertyKey);
  const faviconDarkId = propertyValue(faviconDarkPropertyKey);
  const resolvedLightFaviconId = faviconLightId ?? faviconDarkId ?? faviconId;
  const resolvedDarkFaviconId = faviconDarkId ?? faviconLightId ?? faviconId;

  const savedColorTheme =
    colorThemeOptions.find(o => o.key === branding?.colorTheme)?.key ?? 'default';

  // Requests an upload slot from the tenant-scoped resolver, PUTs the file
  // straight to S3, and returns the FileJet id to persist. Returns null on
  // any failure so callers can bail without mutating.
  const uploadFile = async (file: File): Promise<string | null> => {
    const result = await apollo.query({
      query: uploadInstructionsQuery,
      variables: { accountId, files: [{ name: file.name, type: file.type }] },
      fetchPolicy: 'no-cache'
    });
    const instruction = result.data?.accountBrandingUploadInstructions?.[0];
    if (instruction == null) return null;

    const response = await fetch(instruction.uploadFormat.url, {
      method: instruction.uploadFormat.httpMethod,
      headers: instruction.uploadFormat.headers,
      body: file
    });
    if (!response.ok) return null;

    return instruction.id;
  };

  const uploadLogoVariant = async (file: File, propertyKey: string) => {
    setBusyKey(propertyKey);
    try {
      const uploadedId = await uploadFile(file);
      if (uploadedId == null) return;
      // Mirror the main app: persist both the variant property and the
      // primary Organisation.logoId so the logo renders everywhere.
      await Promise.all([
        updateLogoVariantProperty({
          variables: { accountId, properties: [{ key: propertyKey, value: uploadedId }] }
        }),
        updateLogo({ variables: { accountId, logoId: uploadedId } })
      ]);
      await refetch();
    } finally {
      setBusyKey(null);
    }
  };

  const uploadFaviconVariant = async (file: File, propertyKey: string) => {
    setBusyKey(propertyKey);
    try {
      const uploadedId = await uploadFile(file);
      if (uploadedId == null) return;
      await updateFaviconProperty({
        variables: {
          accountId,
          properties: [
            { key: propertyKey, value: uploadedId },
            { key: faviconPropertyKey, value: uploadedId }
          ]
        }
      });
      await refetch();
    } finally {
      setBusyKey(null);
    }
  };

  const removeVariant = async (propertyKey: string) => {
    setBusyKey(propertyKey);
    try {
      await removeBrandingProperty({
        variables: { accountId, properties: [{ key: propertyKey, value: null }] }
      });
      await refetch();
    } finally {
      setBusyKey(null);
    }
  };

  // Initial page load: wait for the branding query before rendering the
  // variants so we don't flash empty "Upload" rows before the saved assets
  // resolve.
  if (loading && branding == null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <SectionGrid title="Logo" description="Shown in the navigation bar and sign-in screen.">
        <Stack gap={3}>
          <AssetVariant
            label="Light variant"
            previewId={resolvedLightLogoId}
            hash={logo?.hash ?? undefined}
            width={logoPreviewWidth}
            height={logoPreviewHeight}
            removable={logoLightId != null}
            busy={busy}
            loading={busyKey === logoLightPropertyKey}
            onSelect={file => uploadLogoVariant(file, logoLightPropertyKey)}
            onRemove={() => removeVariant(logoLightPropertyKey)}
          />
          <AssetVariant
            label="Dark variant"
            previewId={resolvedDarkLogoId}
            hash={logo?.hash ?? undefined}
            width={logoPreviewWidth}
            height={logoPreviewHeight}
            removable={logoDarkId != null}
            busy={busy}
            loading={busyKey === logoDarkPropertyKey}
            onSelect={file => uploadLogoVariant(file, logoDarkPropertyKey)}
            onRemove={() => removeVariant(logoDarkPropertyKey)}
          />
        </Stack>
      </SectionGrid>

      <Divider sx={{ my: 8 }} />

      <SectionGrid title="Favicon" description="Shown in the browser tab.">
        <Stack gap={3}>
          <AssetVariant
            label="Light variant"
            previewId={resolvedLightFaviconId}
            width={faviconPreviewSize}
            height={faviconPreviewSize}
            removable={faviconLightId != null}
            busy={busy}
            loading={busyKey === faviconLightPropertyKey}
            onSelect={file => uploadFaviconVariant(file, faviconLightPropertyKey)}
            onRemove={() => removeVariant(faviconLightPropertyKey)}
          />
          <AssetVariant
            label="Dark variant"
            previewId={resolvedDarkFaviconId}
            width={faviconPreviewSize}
            height={faviconPreviewSize}
            removable={faviconDarkId != null}
            busy={busy}
            loading={busyKey === faviconDarkPropertyKey}
            onSelect={file => uploadFaviconVariant(file, faviconDarkPropertyKey)}
            onRemove={() => removeVariant(faviconDarkPropertyKey)}
          />
        </Stack>
      </SectionGrid>

      <Divider sx={{ my: 8 }} />

      <SectionGrid title="Color theme" description="Accent color used across the account's app.">
        <Stack direction="row" gap={4} flexWrap="wrap">
          {colorThemeOptions.map(option => {
            const isSelected = savedColorTheme === option.key;
            return (
              <Tooltip key={option.key} title={option.label}>
                <Box
                  role="button"
                  aria-checked={isSelected}
                  tabIndex={0}
                  onClick={async () => {
                    if (busy) return;
                    await updateColorTheme({ variables: { accountId, colorTheme: option.key } });
                    await refetch();
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: option.swatch,
                    cursor: busy ? 'default' : 'pointer',
                    border: theme =>
                      isSelected
                        ? `3px solid ${theme.brandPalette.foreground.accent}`
                        : '3px solid transparent',
                    outline: theme =>
                      isSelected
                        ? `2px solid ${theme.brandPalette.border.neutral}`
                        : '2px solid transparent',
                    transition: 'border 0.15s ease, outline 0.15s ease',
                    '&:hover': {
                      outline: theme => `2px solid ${theme.brandPalette.border.neutral}`
                    }
                  }}
                />
              </Tooltip>
            );
          })}
        </Stack>
        <Typography
          variant="labelSmall"
          sx={{ mt: 3, color: theme => theme.brandPalette.foreground.neutralFaded }}
        >
          {colorThemeOptions.find(o => o.key === savedColorTheme)?.label}
        </Typography>
      </SectionGrid>
    </Box>
  );
}

function SectionGrid({
  title,
  description,
  children
}: {
  readonly title: string;
  readonly description: string;
  readonly children: React.ReactNode;
}) {
  return (
    <Grid container alignItems="flex-start">
      <Grid item xs={12} md={5}>
        <Typography variant="subheadingSmall">{title}</Typography>
        <Typography
          variant="body1"
          sx={{ mt: 1, color: theme => theme.brandPalette.foreground.neutralFaded }}
        >
          {description}
        </Typography>
      </Grid>
      <Grid item xs={12} md={7}>
        {children}
      </Grid>
    </Grid>
  );
}

// One preview + upload + (optional) remove row, shared by every logo and
// favicon variant so the four variants stay visually identical.
function AssetVariant({
  label,
  previewId,
  hash,
  width,
  height,
  removable,
  busy,
  loading,
  onSelect,
  onRemove
}: {
  readonly label: string;
  readonly previewId: string | null;
  readonly hash?: string;
  readonly width: number;
  readonly height: number;
  readonly removable: boolean;
  readonly busy: boolean;
  readonly loading: boolean;
  readonly onSelect: (file: File) => void;
  readonly onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Stack gap={1}>
      <Typography variant="labelSmall">{label}</Typography>
      <Stack direction="row" alignItems="center" gap={4}>
        {previewId != null && (
          <Box
            sx={{
              position: 'relative',
              width,
              height,
              borderRadius: 2,
              overflow: 'hidden',
              border: theme => `1px solid ${theme.brandPalette.border.neutral}`
            }}
          >
            <Img
              src={previewId}
              thumbhash={hash}
              width={width}
              height={height}
              fit="contain"
              alt={label}
            />
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.6)'
                }}
              >
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.svg,.ico"
          style={{ display: 'none' }}
          onChange={event => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file != null) onSelect(file);
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
          onClick={() => inputRef.current?.click()}
        >
          {loading ? 'Uploading…' : previewId != null ? 'Replace' : 'Upload'}
        </Button>
        {removable && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={onRemove}>
            Remove
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

const brandingQuery = gql<AccountBrandingTabRootGQL>`
  query AccountBrandingTabRoot($accountId: ID!) {
    accountBranding(accountId: $accountId) {
      organisationId
      colorTheme
      logo {
        id
        hash
      }
      properties {
        key
        value
      }
    }
  }
`;

const uploadInstructionsQuery = gql<AccountBrandingUploadInstructionsGQL>`
  query AccountBrandingUploadInstructions($accountId: ID!, $files: [FileUploadRequest!]!) {
    accountBrandingUploadInstructions(accountId: $accountId, files: $files) {
      id
      uploadFormat {
        url
        httpMethod
        headers
      }
    }
  }
`;

const colorThemeUpdateMutation = gql<AccountBrandingColorThemeUpdateGQL>`
  mutation AccountBrandingColorThemeUpdate($accountId: ID!, $colorTheme: String) {
    accountBrandingColorThemeUpdate(accountId: $accountId, colorTheme: $colorTheme) {
      organisationId
      colorTheme
      logo {
        id
        hash
      }
      properties {
        key
        value
      }
    }
  }
`;

const logoUpdateMutation = gql<AccountBrandingLogoUpdateGQL>`
  mutation AccountBrandingLogoUpdate($accountId: ID!, $logoId: String) {
    accountBrandingLogoUpdate(accountId: $accountId, logoId: $logoId) {
      organisationId
      colorTheme
      logo {
        id
        hash
      }
      properties {
        key
        value
      }
    }
  }
`;

const propertiesUpdateMutation = gql<AccountBrandingPropertiesUpdateGQL>`
  mutation AccountBrandingPropertiesUpdate(
    $accountId: ID!
    $properties: [BrandingPropertyInput!]!
  ) {
    accountBrandingPropertiesUpdate(accountId: $accountId, properties: $properties) {
      organisationId
      colorTheme
      logo {
        id
        hash
      }
      properties {
        key
        value
      }
    }
  }
`;
